/**
 * IdleSmileController.js
 *
 * Drives the avatar's idle face whenever the model isn't speaking.
 * Renders three additive layers, in stack order:
 *
 *   1. BASE_RESTING_TONE      — constant offsets (brows / cheeks / nose)
 *   2. MICRO_EXPRESSIONS      — brief asymmetric tic every 6–12 s
 *   3. Smile preset           — character's assigned drifting smile,
 *                                scaled by the global + per-group
 *                                intensity multipliers
 *
 * Easing runs in two parallel tracks so brief micro-tics actually
 * reach their target peak: the slow stack (base + smile) eases with
 * `smoothingTauMs` (~1000 ms) while the micro layer eases with
 * `microSmoothingTauMs` (~250 ms). Both eased contributions are
 * summed per shape and clamped to [0,1] before writing. Blinking is
 * owned by BlinkController and is NOT a layer here.
 *
 * Smile intensity multipliers
 * ---------------------------
 * The active smile preset is scaled per-shape before being summed
 * into the slow stack. The final scale for a shape is
 *   smileGlobalMultiplier × groupMultiplier(shape)
 * where the group is determined by name prefix: `brow*` → eyebrows,
 * `eye*` → eyes (excluding `eyeBlink*` which BlinkController owns),
 * `jaw*` → jaw, `cheek*` → cheeks. Anything else (mouth*, nose*) is
 * scaled by the global only. Multipliers only affect the smile
 * layer — base resting tone and micros are untouched.
 *
 * Mouth ownership / gating
 * ------------------------
 * The whole stack stands down (writes zero, hands off) ONLY while
 * the model is speaking, while A2F lipsync is streaming, during the
 * post-speech cooldown, or during the initial-load fade-in delay.
 * Mouse hover, orbit-drag, and chat focus do NOT suppress the layers
 * — the user wanted the face alive whenever the model isn't speaking.
 *
 * Robust to missing shapes: any blendshape name that isn't present
 * in the model's `blendShapeControls` is silently skipped (resolved
 * once and cached as null). Shapes resolve by exact name first, then
 * by lowercase suffix-after-last-dot (matching the same trick
 * A2FEmotionRenderer uses for `blendShape1.mouthSmileLeft` style keys).
 */

import {
  IDLE_SMILE_PRESETS,
  SUBTLE_PRESET_KEYS,
  getIdleSmilePresetKeysForCharacter,
} from "./IdleSmilePresets.js";
import { BASE_RESTING_TONE, MICRO_EXPRESSIONS } from "./IdleFaceLayers.js";
import { A2FLipSyncDriver } from "./A2FLipSyncDriver.js";

const DEFAULT_OPTS = {
  // ----- Smile intensity multipliers (smile preset layer only) -----
  // Global scale applied to every shape in the active smile preset
  // before it's summed into the slow target. Default is a mild boost
  // over the hand-tuned preset weights.
  smileGlobalMultiplier: 1.0,
  // Per-group multipliers stacked on top of the global. Group is
  // determined by shape-name prefix in `_smileGroupFor()`.
  smileEyebrowsMultiplier: 2,
  smileEyesMultiplier: 1.0,
  smileJawMultiplier: 1.0,
  smileCheeksMultiplier: 2,

  // Time constant (ms) for exponential easing toward the slow stack
  // target (base + smile preset).
  smoothingTauMs: 1000,
  // Faster time constant (ms) for the micro-expression layer so a
  // 0.6–1.2 s hold actually reaches its target peak. With tau=250 ms,
  // a 0.6 s hold reaches ~91 % of target (vs. ~45 % at tau=1000 ms).
  microSmoothingTauMs: 250,
  // Cooldown after speech ends before the layers re-engage.
  speakingCooldownMs: 1000,
  // Range (ms) for how long to hold a smile preset before drifting.
  variationMinMs: 4500, //14500
  variationMaxMs: 9000, //15500
  // Probability of picking a "subtle" preset when the character's
  // list mixes subtle and brighter ones.
  subtleBias: 0.5,
  // Initial fade-in delay after model load.
  initialDelayMs: 600,
  // Micro-expression scheduler.
  microIntervalMinMs: 2000, //6000
  microIntervalMaxMs: 3000, //12000
  microHoldMinMs: 3000, //600
  microHoldMaxMs: 6000, //1200
  debug: false,
};

export class IdleSmileController {
  /**
   * @param {object} opts
   * @param {object} opts.blendShapeControls - { name: ctl|ctl[] } map
   * @param {string|null} opts.characterId   - model UUID
   */
  constructor(opts = {}) {
    this.blendShapeControls = opts.blendShapeControls || null;
    this.characterId = opts.characterId || null;

    const o = { ...DEFAULT_OPTS, ...opts };
    this._smileGlobalMultiplier = o.smileGlobalMultiplier;
    this._smileEyebrowsMultiplier = o.smileEyebrowsMultiplier;
    this._smileEyesMultiplier = o.smileEyesMultiplier;
    this._smileJawMultiplier = o.smileJawMultiplier;
    this._smileCheeksMultiplier = o.smileCheeksMultiplier;
    this._smoothingTauMs = o.smoothingTauMs;
    this._microSmoothingTauMs = o.microSmoothingTauMs;
    this._speakingCooldownMs = o.speakingCooldownMs;
    this._variationMinMs = o.variationMinMs;
    this._variationMaxMs = o.variationMaxMs;
    this._subtleBias = o.subtleBias;
    this._initialDelayMs = o.initialDelayMs;
    this._microIntervalMinMs = o.microIntervalMinMs;
    this._microIntervalMaxMs = o.microIntervalMaxMs;
    this._microHoldMinMs = o.microHoldMinMs;
    this._microHoldMaxMs = o.microHoldMaxMs;
    this._debug = o.debug !== false;

    this._presetKeys = getIdleSmilePresetKeysForCharacter(this.characterId);

    // Live state. Two parallel eased maps: the slow stack
    // (base + smile) and the fast micro layer. Each frame
    // we sum them per shape, clamp, and write the result.
    this._currentSlow = {}; // shape -> 0..1 (slow eased contribution)
    this._currentMicro = {}; // shape -> 0..1 (micro eased contribution)
    this._targetPreset = null; // active smile preset weight map
    this._activePresetKey = null;
    this._lastTickMs = 0;
    this._nextVariationAtMs = 0;
    this._lastSpeakingMs = 0;
    this._startWallMs = performance.now();

    // Micro-expression state.
    this._activeMicro = null; // weight map of the currently-eased micro
    this._activeMicroIndex = -1; // index of currently-held micro, or -1
    this._lastMicroIndex = -1; // last picked index, persists across the
    // gap between micros so the next pick can avoid repeating it
    this._microHoldUntilMs = 0; // when we drop the active micro back to zero
    this._nextMicroAtMs = 0; // when to pick the next micro

    // Resolution cache: shape name -> control key (or null).
    this._resolveCache = {};
    this._suffixIndex = null;

    // Track every shape any layer has written so the gated path can
    // clean them all up and the eased decay-to-zero keeps running for
    // shapes that drop out of the active layer set.
    this._everTouched = new Set();

    // True once we've zeroed our footprint and handed the face to
    // the lipsync driver. Reset when the cooldown after speech ends.
    this._handedOff = false;

    this._disposed = false;
    // Driven by the single capped render loop (ModelViewer.animate → update),
    // NOT a private requestAnimationFrame. Running on the shared loop keeps the
    // idle brow / eye writes synced to when frames are actually drawn (and on
    // the same 60 FPS grid as the other facial controllers), eliminating the
    // off-beat jitter the old uncapped self-loop produced on high-refresh /
    // Safari (ProMotion) displays — the same flicker class Task #94 removed
    // for the speech-time emotion overlay, now extended to the idle path.
    this._rafId = null;

    if (this._debug) {
      console.log(
        `[IdleSmile] Initialized character=${this.characterId} ` +
          `presets=[${this._presetKeys.join(", ")}] ` +
          `tau=${this._smoothingTauMs}ms microTau=${this._microSmoothingTauMs}ms ` +
          `cooldown=${this._speakingCooldownMs}ms`,
      );
    }
  }

  /**
   * Replace blendshape controls + character (e.g. when switching avatars).
   */
  setContext({ blendShapeControls, characterId } = {}) {
    if (blendShapeControls !== undefined) {
      this.blendShapeControls = blendShapeControls || null;
    }
    if (characterId !== undefined) {
      this.characterId = characterId || null;
      this._presetKeys = getIdleSmilePresetKeysForCharacter(this.characterId);
    }
    this._resolveCache = {};
    this._suffixIndex = null;
    this._currentSlow = {};
    this._currentMicro = {};
    this._everTouched = new Set();
    this._targetPreset = null;
    this._activePresetKey = null;
    this._nextVariationAtMs = 0;
    this._activeMicro = null;
    this._activeMicroIndex = -1;
    this._lastMicroIndex = -1;
    this._microHoldUntilMs = 0;
    this._nextMicroAtMs = 0;
    this._startWallMs = performance.now();
    this._handedOff = false;
    if (this._debug) {
      console.log(
        `[IdleSmile] setContext character=${this.characterId} ` +
          `presets=[${this._presetKeys.join(", ")}]`,
      );
    }
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    // Best-effort: zero out anything we wrote so we don't leave a
    // baked-in offset on the morph buffer for the next controller.
    if (this.blendShapeControls) {
      for (const shape of this._everTouched) {
        this._applyShape(shape, 0);
      }
    }
  }

  // -------------------- internals --------------------

  /**
   * Returns the per-group smile multiplier for a shape. Group is
   * derived from the ARKit name prefix:
   *   brow*   → eyebrows
   *   eye*    → eyes (BlinkController owns eyeBlink*, but those keys
   *             never appear in smile presets so no special-case)
   *   jaw*    → jaw
   *   cheek*  → cheeks
   *   anything else (mouth*, nose*, ...) → 1.0 (global only)
   */
  _smileGroupMultiplierFor(shapeName) {
    if (!shapeName) return 1.0;
    // Compare on the suffix-after-last-dot in case the shape comes
    // through as e.g. `blendShape1.browInnerUp` — same trick the
    // resolver uses elsewhere.
    const dot = shapeName.lastIndexOf(".");
    const name = dot === -1 ? shapeName : shapeName.substring(dot + 1);
    if (name.length < 3) return 1.0;
    const c = name.charCodeAt(0);
    // 'b' = brow, 'e' = eye, 'j' = jaw, 'c' = cheek
    if (c === 98 && name.startsWith("brow")) {
      return this._smileEyebrowsMultiplier;
    }
    if (c === 101 && name.startsWith("eye")) {
      return this._smileEyesMultiplier;
    }
    if (c === 106 && name.startsWith("jaw")) {
      return this._smileJawMultiplier;
    }
    if (c === 99 && name.startsWith("cheek")) {
      return this._smileCheeksMultiplier;
    }
    return 1.0;
  }

  _isSpeakingOrLipsyncActive() {
    let speaking = false;
    try {
      if (typeof window !== "undefined" && window.TTSActive) speaking = true;
    } catch (_) {}
    let a2f = false;
    try {
      a2f = A2FLipSyncDriver.isAnyActive();
    } catch (_) {}
    return speaking || a2f;
  }

  _pickNextPreset() {
    const keys = this._presetKeys;
    if (!keys || keys.length === 0) return null;
    if (keys.length === 1) return keys[0];

    const subtle = keys.filter((k) => SUBTLE_PRESET_KEYS.has(k));
    const bright = keys.filter((k) => !SUBTLE_PRESET_KEYS.has(k));
    let pool = keys;
    if (subtle.length > 0 && bright.length > 0) {
      pool = Math.random() < this._subtleBias ? subtle : bright;
    }
    let candidate = pool[Math.floor(Math.random() * pool.length)];
    if (candidate === this._activePresetKey && pool.length > 1) {
      const others = pool.filter((k) => k !== this._activePresetKey);
      candidate = others[Math.floor(Math.random() * others.length)];
    }
    return candidate;
  }

  _scheduleNextVariation(now) {
    const span = Math.max(500, this._variationMaxMs - this._variationMinMs);
    this._nextVariationAtMs = now + this._variationMinMs + Math.random() * span;
  }

  _scheduleNextMicro(now) {
    const span = Math.max(
      500,
      this._microIntervalMaxMs - this._microIntervalMinMs,
    );
    this._nextMicroAtMs = now + this._microIntervalMinMs + Math.random() * span;
  }

  _pickMicro() {
    const list = MICRO_EXPRESSIONS;
    if (!list || list.length === 0) return -1;
    if (list.length === 1) return 0;
    // Avoid back-to-back duplicates across micro events. Compare against
    // `_lastMicroIndex`, which persists across the inactive gap (vs.
    // `_activeMicroIndex` which resets to -1 the moment the hold ends).
    let idx = Math.floor(Math.random() * list.length);
    if (idx === this._lastMicroIndex) {
      idx = (idx + 1) % list.length;
    }
    return idx;
  }

  /**
   * Advance the idle face one frame. Called from the single capped render
   * loop (ModelViewer.animate) once per drawn frame, alongside the blink /
   * eye-tracking / emotion controllers, so every facial writer shares one
   * cadence. Self-gates during speech / the post-speech cooldown internally,
   * so the caller invokes it unconditionally.
   *
   * @param {number} now performance.now() of the current render frame.
   */
  update(now) {
    if (this._disposed) return;
    if (!this.blendShapeControls) return;

    const dt = this._lastTickMs
      ? Math.max(1, Math.min(100, now - this._lastTickMs))
      : 16;
    this._lastTickMs = now;

    const speaking = this._isSpeakingOrLipsyncActive();
    if (speaking) this._lastSpeakingMs = now;

    const sinceSpeaking = this._lastSpeakingMs
      ? now - this._lastSpeakingMs
      : Infinity;
    const sinceStart = now - this._startWallMs;

    // Hard stand-down: only while speech/lipsync is active, while the
    // post-speech cooldown is elapsing, or during the initial fade-in
    // delay. We do NOT write any shape, not even an eased decay, to
    // avoid racing A2F's per-frame writes. On the first gated frame
    // we snap our footprint to zero in a single shot for a clean
    // hand-off.
    const gated =
      speaking ||
      sinceSpeaking < this._speakingCooldownMs ||
      sinceStart < this._initialDelayMs;

    if (gated) {
      if (!this._handedOff) {
        for (const shape of this._everTouched) {
          this._applyShape(shape, 0);
          this._currentSlow[shape] = 0;
          this._currentMicro[shape] = 0;
        }
        this._everTouched.clear();
        this._targetPreset = null;
        this._activePresetKey = null;
        this._activeMicro = null;
        this._activeMicroIndex = -1;
        this._microHoldUntilMs = 0;
        this._nextMicroAtMs = 0;
        this._handedOff = true;
        if (this._debug && speaking) {
          console.log("[IdleSmile] handed off to lipsync (zeroed)");
        }
      }
      return;
    }

    // Resuming after cooldown — start a fresh fade-in from zero.
    if (this._handedOff) {
      this._handedOff = false;
      this._targetPreset = null;
      this._activePresetKey = null;
      this._activeMicro = null;
      this._activeMicroIndex = -1;
      this._microHoldUntilMs = 0;
      this._nextMicroAtMs = 0;
      this._nextVariationAtMs = 0;
    }

    // ----- Layer 4: smile preset rotation -----
    if (!this._targetPreset || now >= this._nextVariationAtMs) {
      const key = this._pickNextPreset();
      if (key) {
        this._activePresetKey = key;
        this._targetPreset = IDLE_SMILE_PRESETS[key]?.weights || {};
        this._scheduleNextVariation(now);
        if (this._debug) {
          console.log(`[IdleSmile] → preset "${key}"`);
        }
      }
    }
    const smileMap = this._targetPreset || {};

    // ----- Layer 3: micro-expression scheduler -----
    if (this._activeMicro && now >= this._microHoldUntilMs) {
      // Hold elapsed — drop micro, schedule the next one.
      this._activeMicro = null;
      this._activeMicroIndex = -1;
      this._scheduleNextMicro(now);
    } else if (!this._activeMicro && this._nextMicroAtMs === 0) {
      // First idle frame — schedule the first micro into the future.
      this._scheduleNextMicro(now);
    } else if (!this._activeMicro && now >= this._nextMicroAtMs) {
      const idx = this._pickMicro();
      if (idx >= 0) {
        this._activeMicroIndex = idx;
        this._lastMicroIndex = idx;
        this._activeMicro = MICRO_EXPRESSIONS[idx];
        const holdSpan = Math.max(
          100,
          this._microHoldMaxMs - this._microHoldMinMs,
        );
        this._microHoldUntilMs =
          now + this._microHoldMinMs + Math.random() * holdSpan;
        if (this._debug) {
          console.log(
            `[IdleSmile] micro #${idx}: ${Object.keys(this._activeMicro).join(",")}`,
          );
        }
      }
    }
    const microMap = this._activeMicro || null;

    // ----- Layer 1: base resting tone -----
    const baseMap = BASE_RESTING_TONE;

    // Build two parallel per-shape target maps:
    //   slowTargets  = base + (smile × multipliers)  (eased with slow tau)
    //   microTargets = micro                         (eased with fast tau)
    // The micro layer needs a much faster easing so a 0.6–1.2 s hold
    // actually reaches its target peak; the slow stack stays smooth.
    const slowTargets = {};
    const microTargets = {};
    const accumulate = (map, into) => {
      if (!map) return;
      for (const k in map) {
        into[k] = (into[k] || 0) + map[k];
      }
    };
    accumulate(baseMap, slowTargets);
    // Smile preset is the only layer scaled by the intensity
    // multipliers — base resting tone and micros are untouched.
    if (smileMap) {
      const g = this._smileGlobalMultiplier;
      for (const k in smileMap) {
        const groupMul = this._smileGroupMultiplierFor(k);
        slowTargets[k] = (slowTargets[k] || 0) + smileMap[k] * g * groupMul;
      }
    }
    accumulate(microMap, microTargets);

    const slowAlpha = 1 - Math.exp(-dt / this._smoothingTauMs);
    const microAlpha = 1 - Math.exp(-dt / this._microSmoothingTauMs);

    // Union of (this frame's targets in either map) ∪ (everything
    // we've ever touched) so shapes that just dropped out of the
    // active layer set still ease back to zero instead of snapping.
    const union = new Set(Object.keys(slowTargets));
    for (const k in microTargets) union.add(k);
    for (const k of this._everTouched) union.add(k);

    for (const shape of union) {
      const slowTarget = slowTargets[shape] || 0;
      const microTarget = microTargets[shape] || 0;

      const slowCur = this._currentSlow[shape] || 0;
      const microCur = this._currentMicro[shape] || 0;

      const nextSlow = slowCur + (slowTarget - slowCur) * slowAlpha;
      const nextMicro = microCur + (microTarget - microCur) * microAlpha;
      this._currentSlow[shape] = nextSlow;
      this._currentMicro[shape] = nextMicro;

      // Sum the two eased layers and clamp to [0,1]. Morph weights
      // outside that range cause visible artifacts on most rigs.
      let combined = nextSlow + nextMicro;
      if (combined > 1) combined = 1;
      else if (combined < 0) combined = 0;
      this._applyShape(shape, combined);

      if (slowTarget > 1e-5 || microTarget > 1e-5) {
        this._everTouched.add(shape);
      }

      // Fully retire a shape once both layers' targets and eased
      // values are negligible — keeps `_everTouched` from growing
      // forever as micros pick different shapes over time.
      if (
        slowTarget < 1e-5 &&
        microTarget < 1e-5 &&
        Math.abs(nextSlow) < 1e-3 &&
        Math.abs(nextMicro) < 1e-3
      ) {
        this._currentSlow[shape] = 0;
        this._currentMicro[shape] = 0;
        this._applyShape(shape, 0);
        this._everTouched.delete(shape);
      }
    }
  }

  _buildSuffixIndex() {
    this._suffixIndex = new Map();
    if (!this.blendShapeControls) return;
    const keys = Object.keys(this.blendShapeControls);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const lk = key.toLowerCase();
      if (!this._suffixIndex.has(lk)) this._suffixIndex.set(lk, key);
      const dot = key.lastIndexOf(".");
      if (dot !== -1) {
        const suf = key.substring(dot + 1).toLowerCase();
        if (!this._suffixIndex.has(suf)) this._suffixIndex.set(suf, key);
      }
    }
  }

  _resolve(shapeName) {
    if (this._resolveCache[shapeName] !== undefined) {
      return this._resolveCache[shapeName];
    }
    let resolved = null;
    if (this.blendShapeControls) {
      if (this.blendShapeControls[shapeName]) {
        resolved = shapeName;
      } else {
        if (!this._suffixIndex) this._buildSuffixIndex();
        const lookup = this._suffixIndex.get(shapeName.toLowerCase());
        if (lookup && this.blendShapeControls[lookup]) {
          resolved = lookup;
        }
      }
    }
    this._resolveCache[shapeName] = resolved;
    return resolved;
  }

  _applyShape(shapeName, value) {
    const key = this._resolve(shapeName);
    if (!key) return false;
    const ctl = this.blendShapeControls[key];
    if (!ctl) return false;
    if (Array.isArray(ctl)) {
      for (let i = 0; i < ctl.length; i++) this._setMorph(ctl[i], value);
    } else {
      this._setMorph(ctl, value);
    }
    return true;
  }

  _setMorph(ctl, value) {
    if (!ctl || !ctl.mesh || typeof ctl.index !== "number") return;
    const arr = ctl.mesh.morphTargetInfluences;
    if (!arr || ctl.index < 0 || ctl.index >= arr.length) return;
    arr[ctl.index] = value;
    ctl.value = value;
  }
}
